<script setup lang="ts">
  import { computed } from 'vue'
  import { useFormField } from '@/composables/useFormField'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import { useFieldValidation } from '@/composables/useQuestionnaire'
  import { AlertCircle } from 'lucide-vue-next'

  const props = defineProps({
    field: {
      type: Object,
      default: null
    },
    modelValue: {
      type: [Array, Boolean, String, Number],
      default: () => []
    },
    isPreviewMode: {
      type: Boolean,
      default: false
    },
    descriptionId: {
      type: String,
      required: false
    }
  })

  const emit = defineEmits(['update:modelValue', 'validation-error'])

  const normalizedProps = computed(() => ({
    field: props.field,
    modelValue: Array.isArray(props.modelValue) ? props.modelValue : []
  }))

  const { selectedValue } = useFormField(normalizedProps.value)

  const { validationError, handleInputChange } = useFieldValidation(props, emit)

  const errorId = computed(() => `${props.field.uuid}-error`)
  const ariaDescribedBy = computed(() => {
    const ids: string[] = []
    if (props.descriptionId) ids.push(props.descriptionId)
    if (validationError.value) ids.push(errorId.value)
    return ids.length > 0 ? ids.join(' ') : undefined
  })

  const isSelected = (option: any) => {
    return Array.isArray(selectedValue.value) && selectedValue.value.includes(option)
  }

  const handleOptionChange = (value: boolean, option: any) => {
    if (props.isPreviewMode) return
    if (value) {
      if (!selectedValue.value.includes(option)) {
        selectedValue.value.push(option)
      }
    } else {
      const index = selectedValue.value.indexOf(option)
      if (index > -1) {
        selectedValue.value.splice(index, 1)
      }
    }
    handleInputChange(selectedValue.value)
  }
</script>

<template>
  <fieldset
    class="flex flex-col gap-2"
    :aria-describedby="ariaDescribedBy"
    :aria-invalid="!!validationError || undefined"
  >
    <legend class="sr-only">
      {{ field.label }}
    </legend>
    <div
      v-for="(option, key) in props.field.choices"
      :key="key"
      class="flex items-center gap-2 rounded-lg p-2 transition-colors"
      :class="[
        isSelected(option) ? 'bg-gray-50' : 'bg-white',
        isPreviewMode ? 'cursor-default opacity-60' : 'cursor-pointer'
      ]"
    >
      <CheckboxField
        :name="`${props.field.id}-${key}`"
        :label="option"
        :model-value="isSelected(option)"
        :checked-value="true"
        :unchecked-value="false"
        :disabled="isPreviewMode"
        class="flex-1"
        :data-cy="`CheckBoxV3Checkbox-${field.uuid}-${key}`"
        @update:model-value="(value) => handleOptionChange(value, option)"
      />
    </div>
    <div
      v-if="validationError"
      :id="errorId"
      class="mt-1 flex items-center text-sm text-red-600"
      aria-live="polite"
    >
      <AlertCircle
        class="mr-1 h-4 w-4 flex-shrink-0"
        aria-hidden="true"
      />
      {{ validationError }}
    </div>
  </fieldset>
</template>
